/**
 * Error Code Mapper
 *
 * Maps Payment Orchestrator error codes to CHAT error screen types.
 * Frontend-only logic - backend passes raw error codes without mapping.
 */

import type { ChatErrorScreenType } from '../types/chat-timeline.types';
import { logger } from './logger';

/**
 * Error codes that map to 'retry-later' screen type.
 *
 * Technical/transient failures (NOT bank rejection).
 * Message: "Please, request that the customer try again"
 * Behavior: Keep all fields filled, customer retries with same details.
 */
const RETRY_LATER_CODES = [
  // Gateway/Network errors
  'GATEWAY_TIMEOUT',
  'GATEWAY_ERROR',
  'SERVICE_UNAVAILABLE',
  'NETWORK_ERROR',
  'TIMEOUT',
  'CONNECTION_ERROR',

  // Processing errors
  'PROCESSING_ERROR',
  'TEMPORARY_ERROR',
  'SYSTEM_ERROR',
  'UNKNOWN_ERROR',

  // Rate limiting
  'RATE_LIMIT_EXCEEDED',
  'TOO_MANY_REQUESTS',
] as const;

/**
 * Error codes that map to 'dont-retry' screen type.
 *
 * Bank authorization failures (declined, insufficient funds, restrictions).
 * Message: "Request the customer to try again using a different payment method"
 * Behavior: Clear all card entry fields, customer must use different card.
 */
const DONT_RETRY_CODES = [
  // Card declined by bank
  'CARD_DECLINED',
  'DO_NOT_HONOR',
  'DECLINED',

  // Insufficient funds
  'INSUFFICIENT_FUNDS',
  'EXCEEDS_WITHDRAWAL_LIMIT',

  // Invalid card data
  'INVALID_CARD',
  'INVALID_CARD_NUMBER',
  'EXPIRED_CARD',
  'CARD_EXPIRED',

  // Card restrictions
  'CARD_RESTRICTED',
  'RESTRICTED_CARD',
  'CARD_NOT_SUPPORTED',
  'CARD_BLOCKED',
  'LOST_CARD',
  'STOLEN_CARD',
  'SUSPECTED_FRAUD',

  // Invalid security data
  'INVALID_CVV',
  'INVALID_CVC',
  'CVV_MISMATCH',
  'INVALID_EXPIRY',
  'EXPIRY_DATE_INVALID',

  // 3DS failures
  'AUTHENTICATION_FAILED',
  '3DS_FAILED',

  // Invalid amount
  'INVALID_AMOUNT',
  'AMOUNT_TOO_HIGH',
  'AMOUNT_TOO_LOW',
] as const;

/**
 * Map error code to error screen type.
 *
 * Logic:
 * 1. If errorCode is MAX_ATTEMPTS_EXCEEDED: 'max-attempts'
 * 2. If errorCode in DONT_RETRY_CODES: 'dont-retry'
 * 3. If errorCode in RETRY_LATER_CODES: 'retry-later'
 * 4. Default: 'retry-later' (conservative fallback)
 *
 * NOTE: Max attempts detection removed from frontend.
 * Payment Orchestrator is source of truth for attempt limits.
 * Frontend should receive 'MAX_ATTEMPTS_EXCEEDED' error code from backend.
 *
 * @param errorCode - Error code from Payment Orchestrator
 * @returns Error screen type
 *
 * @example
 * mapErrorCodeToScreenType('GATEWAY_TIMEOUT') // 'retry-later'
 * mapErrorCodeToScreenType('CARD_DECLINED')   // 'dont-retry'
 * mapErrorCodeToScreenType('MAX_ATTEMPTS_EXCEEDED') // 'max-attempts'
 */
export function mapErrorCodeToScreenType(
  errorCode: string
): ChatErrorScreenType {
  // Normalize error code (uppercase, trim)
  const normalizedCode = errorCode.toUpperCase().trim();

  logger.debug('Mapping error code to screen type', {
    errorCode: normalizedCode,
  });

  // Check for explicit max attempts signal from backend
  if (
    normalizedCode === 'MAX_ATTEMPTS_EXCEEDED' ||
    normalizedCode.includes('MAX_ATTEMPT')
  ) {
    logger.info('Max attempts exceeded (from backend)', {
      errorCode: normalizedCode,
    });
    return 'max-attempts';
  }

  // Check dont-retry codes (terminal errors)
  if (
    DONT_RETRY_CODES.includes(
      normalizedCode as (typeof DONT_RETRY_CODES)[number]
    )
  ) {
    logger.info('Terminal error detected', { errorCode: normalizedCode });
    return 'dont-retry';
  }

  // Check retry-later codes (transient errors)
  if (
    RETRY_LATER_CODES.includes(
      normalizedCode as (typeof RETRY_LATER_CODES)[number]
    )
  ) {
    logger.info('Transient error detected', { errorCode: normalizedCode });
    return 'retry-later';
  }

  // Default: retry-later (conservative - don't clear fields by default)
  logger.warn('Unknown error code, defaulting to retry-later', {
    errorCode: normalizedCode,
  });

  return 'retry-later';
}

/**
 * Get user-friendly error message for error screen.
 *
 * Provides default messages matching design specs.
 *
 * @param errorType - Error screen type
 * @param errorCode - Error code from backend (optional)
 * @param backendMessage - Error message from backend (optional)
 * @returns User-friendly error message
 */
export function getErrorMessage(
  errorType: ChatErrorScreenType,
  _errorCode?: string,
  backendMessage?: string
): string {
  // Use backend message if available
  if (backendMessage) {
    return backendMessage;
  }

  // Default messages by error type (from design specs)
  switch (errorType) {
    case 'retry-later':
      // Technical problems (timeout, gateway error, etc) - NOT bank rejection
      return 'Please, request that the customer try again';

    case 'dont-retry':
      // Bank authorization error (declined, no funds, bank blocked, etc)
      return 'Request the customer to try again using a different payment method';

    case 'max-attempts':
      // Maximum attempts exceeded (signaled by Payment Orchestrator)
      return 'The payment session has ended. Please create a new payment or direct the customer to contact their bank.';

    case 'session-expired':
      return 'This payment session is no longer valid. Please generate a new payment request.';

    case 'sse-disconnected':
      return 'The connection to the payment session was lost. Please reload the page to reconnect.';

    default:
      return 'Please, request that the customer try again';
  }
}

/**
 * Get error screen title.
 *
 * @param errorType - Error screen type
 * @returns Error screen title
 */
export function getErrorTitle(errorType: ChatErrorScreenType): string {
  switch (errorType) {
    case 'retry-later':
      return 'This payment could not be processed';

    case 'dont-retry':
      return 'This payment could not be processed';

    case 'max-attempts':
      return 'This card was declined';

    case 'session-expired':
      return 'Your session has expired';

    case 'sse-disconnected':
      return 'Connection lost';

    default:
      return 'This payment could not be processed';
  }
}

/**
 * Get button label for error screen.
 *
 * @param errorType - Error screen type
 * @returns Button label
 */
export function getButtonLabel(errorType: ChatErrorScreenType): string {
  if (errorType === 'session-expired') return 'Close';
  if (errorType === 'sse-disconnected') return 'Reload';
  return 'Go back';
}
